import '../../../shared/StyledInput.css';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Pill } from '../../../shared/Pill';
import Accordion from '../../../shared/Accordion';
import ToggleSwitch from '../../../shared/ToggleSwitch';
import SaveButton from '../../../shared/SaveButton';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { todayInTenant, formatDateTime } from '../../../shared/tenantClock';
import DateInput from '../../../shared/DateInput';
import MailboxFolderPicker from '../../../shared/MailboxFolderPicker';
import { usePermissions } from '../../../shared/PermissionsContext';
import QuickBooksCard from './QuickBooksCard';
import AustraliaPostCard from './AustraliaPostCard';

// Registry of known third-party APIs (issue #203 — Google Places is the
// first; Australia Post etc. get added here later). Purely UI copy — the
// backend (api_integrations table + apiIntegrationController.js) is
// provider-agnostic, so a new entry here needs no schema change, just its
// own service module and a case in apiIntegrationController's
// PROVIDER_TESTS if it wants a "Test Connection" action, and/or
// PROVIDER_SYNCS if it wants a "Sync Now" action.
//
// `fields` drives ProviderCard's form: one entry per input. `secret: true`
// marks the one field (per provider, at most) that maps to the api_key
// column — write-only, blank on load, blank stays blank on save. Every
// other field round-trips through the `config` JSON column and is safe to
// pre-fill from what's already saved.
// issue #222 — check frequency is stored as minutes-between-checks so the
// backend never has to interpret unit words; the dropdown just maps each
// offered choice to its minute count.
const CHECK_FREQUENCY_OPTIONS = [
  { value: '5', label: '5 min' },
  { value: '15', label: '15 min' },
  { value: '30', label: '30 min' },
  { value: '60', label: '1 hr' },
  { value: '180', label: '3 hr' },
  { value: '360', label: '6 hr' },
  { value: '720', label: '12 hr' },
  { value: '1440', label: 'Daily' },
];

const todayIso = () => todayInTenant();

const PROVIDERS = [
  {
    key: 'google_places',
    label: 'Google Places',
    description: 'Looks up Opening Hours automatically for a Prospect/Customer with an address on file (CRM > Prospects & Customers). Falls back to manual entry when no key is configured or nothing is found.',
    testable: true,
    fields: [
      { key: 'api_key', label: 'API Key', secret: true, help: 'A Places API (New) key, restricted to that API. Create one in Google Cloud Console.' },
    ],
  },
  {
    key: 'mailbox_imap',
    label: 'Mailbox (IMAP)',
    description: 'Reads the shared contact@ mailbox via IMAP so its email can be matched and logged against Commercial Entities (Email Sync), and sends via SMTP for Compose Email (Commercial Entity Activity view). Staff mailboxes connected from My Account use the same IMAP host, port and check frequency, and stop syncing while this is switched off. The connection is always encrypted — there’s no separate security-mode choice to make.',
    testable: true,
    syncable: true,
    folderPicker: true,
    fields: [
      { key: 'host', label: 'IMAP Host', placeholder: 'imap.example.com' },
      { key: 'port', label: 'IMAP Port', placeholder: '993' },
      { key: 'username', label: 'Username', placeholder: 'contact@example.com' },
      { key: 'password', label: 'Password', secret: true },
      { key: 'smtp_host', label: 'SMTP Host', placeholder: 'smtp.example.com', help: 'Often a different host/port to IMAP above, even for the same mailbox. Required for Compose Email to be able to send.' },
      { key: 'smtp_port', label: 'SMTP Port', placeholder: '587' },
      { key: 'history_start_date', label: 'History start date', type: 'date', default: todayIso, help: 'How far back the initial mailbox pull goes. Cannot be a future date.' },
      { key: 'check_frequency_minutes', label: 'Check frequency', type: 'select', default: '15', options: CHECK_FREQUENCY_OPTIONS },
    ],
  },
];

// Secret fields (`secret: true`) start blank — they're never sent back by
// the API — and stay blank after every save. Every other field pre-fills
// from the provider's stored `config`, falling back to the field's own
// `default` (a value or a value-returning function, e.g. "today") when it
// hasn't been configured yet, so editing one setting doesn't require
// retyping the rest.
function initFieldValues(provider, row) {
  const values = {};
  provider.fields.forEach((f) => {
    if (f.secret) { values[f.key] = ''; return; }
    const saved = row?.config?.[f.key];
    if (saved !== undefined && saved !== null && saved !== '') { values[f.key] = saved; return; }
    values[f.key] = typeof f.default === 'function' ? f.default() : (f.default ?? '');
  });
  return values;
}

// The shared mailbox's synced folders in words (issue #262) — the saved
// choice, or the defaults until one is saved. Mirrors the backend's
// mailboxFolders.describeSelection.
function foldersSummary(config) {
  if (!config?.sync_folders) {
    return config?.folder_defaults === 'inbox_sent' ? 'Inbox, Sent' : 'Inbox, Sent, Archive + subfolders (default)';
  }
  return config.sync_folders
    .map(({ path, subfolders }) => `${path.toUpperCase() === 'INBOX' ? 'Inbox' : path.replace(/^INBOX[./]/i, '')}${subfolders ? ' + subfolders' : ''}`)
    .join(', ');
}

function ProviderCard({ provider, row, onSaved }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [values, setValues] = useState(() => initFieldValues(provider, row));
  const [enabled, setEnabled] = useState(row?.enabled ?? true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [choosingFolders, setChoosingFolders] = useState(false);
  const [foldersNotice, setFoldersNotice] = useState('');

  const configured = !!row?.configured;

  const setField = (key, val) => setValues((prev) => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = { enabled };
      const config = {};
      let hasConfigFields = false;
      provider.fields.forEach((f) => {
        const val = (values[f.key] || '').trim();
        if (f.secret) {
          // Blank stays blank on purpose — an already-configured secret
          // isn't touched unless the user actually types a replacement, so
          // Save never has to be treated as "clear it" by accident.
          if (val) body.api_key = val;
        } else {
          hasConfigFields = true;
          config[f.key] = val;
        }
      });
      if (hasConfigFields) body.config = config;
      const res = await axios.put(`/api/api-integrations/${provider.key}`, body);
      onSaved(res.data.data);
      setValues((prev) => {
        const next = { ...prev };
        provider.fields.forEach((f) => { if (f.secret) next[f.key] = ''; });
        return next;
      });
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`/api/api-integrations/${provider.key}/test`);
      onSaved(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Test failed');
    }
    setTesting(false);
  };

  // Runs the provider's scheduled poll immediately rather than waiting for
  // its next due tick (emailSyncService.pollTenant bypasses isDue when
  // called this way) — e.g. to see a just-sent test email land as an
  // Activity without waiting out the Check frequency.
  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`/api/api-integrations/${provider.key}/sync-now`);
      if (res.data.data) onSaved(res.data.data);
      setSyncResult({ ok: res.data.ok, message: res.data.message });
      dismissError();
    } catch (err) {
      showError(err, 'Sync failed');
    }
    setSyncing(false);
  };

  const title = (
    <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {provider.label}
      {configured ? (
        <Pill color="#1a7f4b" background="rgba(26, 127, 75, 0.12)">Configured</Pill>
      ) : (
        <Pill color="#5a6b85" background="#dde3ea">Not configured</Pill>
      )}
    </span>
  );

  return (
    <Accordion title={title} defaultOpen={false}>
      <div style={{ fontSize: 13, color: '#666', marginTop: -6, marginBottom: 12, maxWidth: 480 }}>{provider.description}</div>

      {provider.fields.map((f) => (
        <div key={f.key} style={{ marginTop: f === provider.fields[0] ? 0 : 12 }}>
          <label style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>{f.label}</label>
          {f.type === 'select' ? (
            <select
              className="styled-input"
              value={values[f.key]}
              onChange={(e) => setField(f.key, e.target.value)}
              style={{ width: '100%' }}
            >
              {f.options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          ) : f.type === 'date' ? (
            <DateInput
              value={values[f.key]}
              onChange={(e) => setField(f.key, e.target.value)}
              max={todayIso()}
              style={{ width: 160 }}
            />
          ) : (
            <input
              className="styled-input"
              type={f.secret ? 'password' : (f.type || 'text')}
              value={values[f.key]}
              onChange={(e) => setField(f.key, e.target.value)}
              placeholder={f.secret && configured ? `•••••••••••••• (configured — enter a new ${f.label.toLowerCase()} to replace it)` : (f.placeholder || (f.secret ? `Paste ${f.label.toLowerCase()}` : ''))}
              style={{ width: '100%' }}
              autoComplete="off"
            />
          )}
          {f.help && <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{f.help}</div>}
        </div>
      ))}

      {provider.folderPicker && (
        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>Folders</label>
          <div style={{ fontSize: 14 }}>
            {foldersSummary(row?.config)}
            {!choosingFolders && configured && (
              <button
                type="button"
                onClick={() => { setChoosingFolders(true); setFoldersNotice(''); }}
                title="Choose which folders of this mailbox are synced"
                style={{ marginLeft: 10, background: 'none', border: 'none', padding: 0, color: '#1d4ed8', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}
              >
                Change
              </button>
            )}
          </div>
          {!configured && <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Save the mailbox settings first to choose folders.</div>}
          {foldersNotice && <div style={{ fontSize: 13, color: '#1a7f4b', marginTop: 4 }}>{foldersNotice}</div>}
          {choosingFolders && (
            <MailboxFolderPicker
              endpoint={`/api/api-integrations/${provider.key}/folders`}
              onSaved={(res) => {
                setChoosingFolders(false);
                setFoldersNotice(res.message);
                onSaved({ ...row, config: { ...(row?.config || {}), sync_folders: res.data.selection } });
              }}
              onCancel={() => setChoosingFolders(false)}
            />
          )}
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <ToggleSwitch checked={enabled} onChange={setEnabled} label="Enabled" />
      </div>

      {row?.last_tested_at && (
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          {row.last_test_ok ? <CheckCircle2 size={15} color="#1a7f4b" /> : <XCircle size={15} color="#b91c1c" />}
          <span style={{ color: row.last_test_ok ? '#1a7f4b' : '#b91c1c' }}>{row.last_test_message}</span>
          <span style={{ color: '#999' }}>· {formatDateTime(row.last_tested_at)}</span>
        </div>
      )}

      {row?.last_synced_at && (
        <div style={{ marginTop: 6, fontSize: 13, color: '#999' }}>
          Last synced {formatDateTime(row.last_synced_at)}
        </div>
      )}

      {syncResult && (
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          {syncResult.ok ? <CheckCircle2 size={15} color="#1a7f4b" /> : <XCircle size={15} color="#b91c1c" />}
          <span style={{ color: syncResult.ok ? '#1a7f4b' : '#b91c1c' }}>{syncResult.message}</span>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <SaveButton type="button" onClick={handleSave} loading={saving} />
        {provider.testable && (
          <button
            type="button"
            onClick={handleTest}
            disabled={testing || !configured}
            title={!configured ? 'Save credentials first' : undefined}
            style={{ marginLeft: 8, padding: '7px 14px', borderRadius: 6, border: '1px solid #bfc8d6', background: '#fff', color: '#333', fontWeight: 600, fontSize: 13, cursor: (testing || !configured) ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            {testing && <Loader2 size={14} strokeWidth={2.5} style={{ animation: 'api-integrations-spin 0.8s linear infinite' }} />}
            {testing ? 'Testing...' : 'Test Connection'}
          </button>
        )}
        {provider.syncable && (
          <button
            type="button"
            onClick={handleSyncNow}
            disabled={syncing || !configured || !enabled}
            title={!configured ? 'Save credentials first' : (!enabled ? 'Enable the integration first' : undefined)}
            style={{ marginLeft: 8, padding: '7px 14px', borderRadius: 6, border: '1px solid #bfc8d6', background: '#fff', color: '#333', fontWeight: 600, fontSize: 13, cursor: (syncing || !configured || !enabled) ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            {syncing && <Loader2 size={14} strokeWidth={2.5} style={{ animation: 'api-integrations-spin 0.8s linear infinite' }} />}
            {syncing ? 'Syncing...' : 'Sync Now'}
          </button>
        )}
      </div>
    </Accordion>
  );
}

// Platform > API Integrations (issue #203) — a generic home for every
// third-party API credential the app uses, so a new one (Australia Post,
// etc.) gets its own card here rather than a bespoke settings field bolted
// onto whichever module happens to need it first.
function ApiIntegrationsPage() {
  const showError = useErrorToast();
  const { isAdmin } = usePermissions();
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/api-integrations')
      .then((res) => {
        const byProvider = {};
        (res.data.data || []).forEach((row) => { byProvider[row.provider] = row; });
        setRows(byProvider);
      })
      .catch((err) => showError(err, 'Failed to load API Integrations'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  if (loading) {
    return <div style={{ width: '100%', maxWidth: 640, margin: '32px auto' }}>Loading...</div>;
  }

  return (
    <div style={{ width: '100%', maxWidth: 640, margin: '32px auto 48px' }}>
      <style>{'@keyframes api-integrations-spin { to { transform: rotate(360deg); } }'}</style>
      <h2 style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 20 }}>API Integrations</h2>
      <p style={{ color: '#666', fontSize: 14, marginTop: 0, marginBottom: 20 }}>
        Third-party API keys used across the app. Each key is stored server-side and never shown again once saved.
      </p>
      {PROVIDERS.map((provider) => (
        <ProviderCard
          key={provider.key}
          provider={provider}
          row={rows[provider.key]}
          onSaved={(row) => setRows((prev) => ({ ...prev, [provider.key]: row }))}
        />
      ))}
      {isAdmin && <AustraliaPostCard />}
      {isAdmin && <QuickBooksCard />}
    </div>
  );
}

export default ApiIntegrationsPage;
